import {AnalyticsBrowser} from '@segment/analytics-next'
import {useEnv} from '@/composables/use-env.js'
import {useTeams} from '@/composables/use-teams.js'
import {useRouting} from '@/composables/use-routing.js'
import {useTimezone} from '@/composables/use-timezone.js'
import {useCurrentUser} from '@/composables/use-current-user.js'
import {useCurrentOrganization} from '@/composables/use-current-organization.js'
import {watch} from 'vue'

/**
 * Segment analytics composable
 */
export function useSegment() {

  //Extract refs
  const {isProduction, isTest, gitHash} = useEnv()
  const {user, locale} = useCurrentUser()
  const {organization} = useCurrentOrganization()
  const {timezone} = useTimezone()
  const {route} = useRouting()
  const {isInTeams, userId, tenantId, teamsAppId, teamsAppVersion} = useTeams()

  //Determine write key
  const writeKey = isProduction.value ?
    '7NMPz7VTUJbXJ4Mx3bzTJcoSOp70T2xV' : //Production
    'SsUYtAucTCIaN3IeiTPEEZzbCMjjOBL2' //Non-prod

  //Initialize analytics
  const analytics = new AnalyticsBrowser()

  //Load and apply watcher when not in test env
  if (!isTest.value) {
    analytics.load({writeKey})
    watch(route, () => {
      if (route?.name) {
        analytics.page(route.name, route.params)
      }
    }, {immediate: true})
  }

  //Identify helper
  const identify = () => {

    //Ignore in test mode
    if (isTest.value) {
      return
    }

    //Must be called after user/org have been loaded
    if (!user.value || !organization.value) {
      throw new Error('User and organization must be loaded before calling identify')
    }

    //Call
    analytics.identify(user.value.id, {

      //User context
      locale: locale.value,
      timezone: timezone.value,

      //Organization context
      organizationId: organization.value.id,
      organizationName: organization.value.name,

      //App context
      appVersion: gitHash.value,
      appContext: isInTeams.value ? 'teams' : 'web',

      //Teams context
      teams: {
        appId: teamsAppId.value,
        appVersion: teamsAppVersion.value,
        tenantId: tenantId.value,
        userId: userId.value,
      },
    })
  }

  //Track helper
  const track = (event, properties) => {

    //Ignore in test mode
    if (isTest.value) {
      return
    }

    //Track
    analytics.track(event, properties)
  }

  //Expose interface
  return {
    analytics,
    identify,
    track,
  }
}
